import { useState } from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";

const SPACE_URL = "https://space.artpart.at";
const LIMIT = 250;

export const CognitiveSpaceWidget = () => {
  const [text, setText] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    const value = text.trim();
    if (!value || status === "sending") return;

    setStatus("sending");
    setMessage("");

    try {
      const response = await fetch(`${SPACE_URL}/api/spaces/artpart/thoughts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ text: value }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) throw new Error(data?.detail || "Der Gedanke konnte gerade nicht gesendet werden.");

      if (data.status === "accepted") {
        setStatus("success");
        setMessage("Dein Gedanke ist jetzt Teil des Raums.");
        setText("");
      } else {
        setStatus("idle");
        setMessage(data.message || "Dieser Gedanke wurde nicht übernommen.");
      }
    } catch (error) {
      setStatus("error");
      setMessage(error?.message || "Der Gedanke konnte gerade nicht gesendet werden.");
    }
  };

  return (
    <section
      data-testid="cognitive-space-widget"
      className="relative overflow-hidden border-b border-obsidian-line bg-black ap-section"
    >
      <div className="pointer-events-none absolute inset-0 ap-grid-bg-dark opacity-40" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-20 top-1/2 h-52 w-52 -translate-y-1/2 rounded-full bg-signal/5 blur-3xl" aria-hidden="true" />

      <div className="ap-container relative grid items-center gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-14">
        <div>
          <p className="ap-stamp mb-4 text-signal">COGNITIVE SPACE · ARTPART</p>
          <h2 className="ap-h2 max-w-md text-paper">TEILE EINEN GEDANKEN</h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-paper/65 sm:text-base">
            Anonym und ohne Konto. Dein Gedanke wird semantisch mit anderen Stimmen verbunden und kann Teil eines wachsenden digitalen Kunstwerks werden.
          </p>
          <a
            href={SPACE_URL}
            target="_blank"
            rel="noreferrer"
            className="ap-stamp mt-6 inline-flex items-center gap-2 text-paper/60 transition-colors hover:text-signal"
          >
            Cognitive Space betreten <ArrowUpRight size={13} />
          </a>
        </div>

        <form onSubmit={submit} className="border border-paper/15 bg-paper/[0.025] p-4 sm:p-5">
          <label htmlFor="cognitive-space-thought" className="sr-only">
            Einen Gedanken teilen
          </label>
          <textarea
            id="cognitive-space-thought"
            value={text}
            onChange={(event) => setText(event.target.value)}
            maxLength={LIMIT}
            rows={3}
            placeholder="Ein Gedanke, ein Gefühl oder eine Beobachtung …"
            className="min-h-28 w-full resize-none border border-paper/15 bg-black/55 px-4 py-3 font-body text-base leading-relaxed text-paper outline-none transition-colors placeholder:text-paper/35 focus:border-signal/60"
          />

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-xl text-[11px] leading-relaxed text-paper/45">
              Bitte keine Namen, Adressen oder Kontaktdaten eingeben. Der Text wird automatisiert semantisch verarbeitet.{" "}
              <a href={`${SPACE_URL}/datenschutz`} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-paper/70">
                Datenschutz
              </a>
            </p>
            <span className="ap-stamp text-paper/35">{text.length} / {LIMIT}</span>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={!text.trim() || status === "sending"}
              className="inline-flex min-h-11 items-center justify-center border border-paper bg-paper px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-black transition-opacity disabled:cursor-not-allowed disabled:opacity-35"
            >
              {status === "sending" ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Sendet …
                </>
              ) : (
                "Gedanken senden"
              )}
            </button>

            {message && (
              <p className={`text-sm ${status === "success" ? "text-signal" : status === "error" ? "text-red-300" : "text-paper/60"}`}>
                {message}
              </p>
            )}
          </div>

          {status === "success" && (
            <a
              href={SPACE_URL}
              target="_blank"
              rel="noreferrer"
              className="ap-stamp mt-4 inline-flex items-center gap-2 text-signal"
            >
              Im Raum ansehen <ArrowUpRight size={13} />
            </a>
          )}
        </form>
      </div>
    </section>
  );
};
